import { motion } from "framer-motion";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { fmtINR, fmtINRCompact } from "@/homeiq/services/format";

const EASE = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay, ease: EASE }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Budget context strip (horizon-aware; "Change in Market" jumps user there)
// ─────────────────────────────────────────────────────────────────────
function BudgetContextStrip() {
    const { affordableBudget, segment, wealthYear, setActiveSection, investmentOutputs } = useHomeIQ();
    const pinned = investmentOutputs.marketChoice?.source === "pinned" ? investmentOutputs.marketChoice.market : null;
    return (
        <Reveal>
            <div
                data-testid="inv-budget-strip"
                className="rounded-2xl border border-[#0A1F44]/10 bg-white px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
            >
                <div className="flex items-baseline gap-3 flex-wrap">
                    <div>
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Your budget</span>
                        <span className="ml-2 font-display font-extrabold text-xl text-[#0A1F44]">{fmtINR(affordableBudget)}</span>
                    </div>
                    <span className="hidden md:inline text-[#0A1F44]/20">·</span>
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E6BFF]/10 text-[11px] font-semibold text-[#1E6BFF]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1E6BFF]" aria-hidden />
                        {segment}
                    </span>
                    {pinned && (
                        <span data-testid="inv-pinned-pill" className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#16a34a]/12 text-[11px] font-semibold text-[#16a34a] border border-[#16a34a]/30">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#16a34a]" aria-hidden />
                            Pinned · {pinned.area} ({pinned.growth}% CAGR)
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <span
                        data-testid="inv-horizon-label"
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A1F44]/6 text-[11px] font-semibold text-[#0A1F44]"
                    >
                        Viewing <span className="text-[#1E6BFF] tabular-nums">{wealthYear}-year</span> horizon
                    </span>
                    <button
                        type="button"
                        data-testid="inv-change-horizon"
                        onClick={() => setActiveSection("market")}
                        className="text-[11px] font-semibold text-[#1E6BFF] hover:text-[#0A1F44] underline-offset-2 hover:underline transition-colors"
                    >
                        Change in Market →
                    </button>
                </div>
            </div>
        </Reveal>
    );
}

function SectionHead({ eyebrow, title, sub }) {
    return (
        <div className="mb-2">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold mb-2">— {eyebrow}</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0A1F44] tracking-tight">{title}</h2>
            {sub && <p className="mt-3 text-[#0A1F44]/65 max-w-2xl text-sm md:text-base">{sub}</p>}
        </div>
    );
}

function PillarHead({ index, title, sub }) {
    return (
        <div className="flex items-baseline gap-3 mb-4">
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold tabular-nums">
                {String(index).padStart(2, "0")}
            </span>
            <div>
                <h3 className="font-display font-extrabold text-2xl text-[#0A1F44]">{title}</h3>
                {sub && <p className="text-sm text-[#0A1F44]/65 mt-0.5">{sub}</p>}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Blocked state (no rent entered)
// ─────────────────────────────────────────────────────────────────────
function BlockedPanel() {
    const { setActiveSection } = useHomeIQ();
    return (
        <Reveal>
            <div data-testid="inv-blocked" className="rounded-3xl border border-dashed border-[#0A1F44]/20 bg-[#F7F8FB] p-8 md:p-12 text-center">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#1E6BFF] font-semibold">— Awaiting rent</div>
                <h3 className="mt-3 font-display font-extrabold text-2xl md:text-3xl text-[#0A1F44]">Tell us what you pay in rent today</h3>
                <p className="mt-3 text-[#0A1F44]/65 max-w-xl mx-auto">
                    Every projection below is anchored to the gap between your rent and your EMI. Add it on the home screen to unlock Wealth Projection, Buy vs Rent, Cost of Waiting and the HomeIQ Recommendation.
                </p>
                <button
                    type="button"
                    onClick={() => setActiveSection("home")}
                    data-testid="inv-blocked-cta"
                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0A1F44] hover:bg-[#1E6BFF] text-white text-sm font-semibold transition-colors"
                >
                    Enter rent on home screen →
                </button>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Pillar 1 — Wealth Projection (LIVE)
// ─────────────────────────────────────────────────────────────────────
function WealthProjection() {
    const { wealthYear, investmentOutputs, affordabilityOutputs } = useHomeIQ();
    const { ladder, propertyCAGR, equityCAGR } = investmentOutputs;
    const checkpointYears = [5, 10, 15, 20];
    const checkpoints = checkpointYears
        .map((y) => ladder.find((r) => r.year === y))
        .filter(Boolean);
    const current = ladder.find((r) => r.year === wealthYear) ?? checkpoints[1];
    const maxBuyWorth = Math.max(...checkpoints.map((r) => r.netBuyWorth));

    return (
        <Reveal>
            <div data-testid="inv-pillar-wealth" className="space-y-6">
                <PillarHead index={1} title="Wealth Projection" sub="What this home becomes — and how the same rupee performs as a renter-investor." />

                {/* Hero — buyer net worth at chosen horizon */}
                <div data-testid="inv-trajectory" className="rounded-3xl bg-[#0A1F44] text-white p-6 md:p-10 relative overflow-hidden">
                    <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
                    <div className="relative grid md:grid-cols-2 gap-8 items-center">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Wealth trajectory · buyer</p>
                            <h3 className="mt-2 font-display font-extrabold text-3xl md:text-4xl">
                                In {wealthYear} years your home equity becomes
                            </h3>
                            <div className="mt-4 font-display font-black text-5xl md:text-6xl tracking-tight tabular-nums">
                                {fmtINR(current.netBuyWorth)}
                            </div>
                            <div className="mt-2 text-white/55 text-sm">
                                Property value {fmtINRCompact(current.propertyValue)} − outstanding loan {fmtINRCompact(current.outstandingLoan)} · {propertyCAGR.toFixed(1)}% CAGR
                            </div>
                        </div>
                        <div className="relative h-56">
                            <div className="absolute inset-0 flex items-end gap-3">
                                {checkpoints.map((r) => {
                                    const active = r.year === wealthYear;
                                    return (
                                        <div key={r.year} className="flex-1 flex flex-col items-center gap-2">
                                            <motion.div
                                                initial={{ height: 0 }}
                                                whileInView={{ height: `${(r.netBuyWorth / maxBuyWorth) * 100}%` }}
                                                viewport={{ once: true }}
                                                transition={{ duration: 1.1, ease: EASE }}
                                                className={`w-full rounded-t-xl ${active ? "bg-gradient-to-t from-[#1E6BFF] to-[#7FA8FF]" : "bg-white/15"}`}
                                                style={{ minHeight: 12 }}
                                                data-testid={`inv-traj-bar-${r.year}`}
                                            />
                                            <div className={`text-xs ${active ? "text-white" : "text-white/55"}`}>{r.year}y</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Side-by-side: Buy net worth vs Rent net worth at chosen horizon */}
                <div data-testid="inv-asset-comparison" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                    <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                        <div>
                            <h4 className="font-display font-extrabold text-xl text-[#0A1F44]">Buy vs Renter-Investor · year {wealthYear}</h4>
                            <p className="text-sm text-[#0A1F44]/65 mt-0.5">Same monthly outflow, two different net worths.</p>
                        </div>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 font-semibold">Live</span>
                    </div>
                    <div className="mt-5 space-y-3">
                        {[
                            { id: "buy", label: "Buyer net worth", sub: "Property − outstanding loan", value: current.netBuyWorth, accent: "#1E6BFF" },
                            { id: "rent", label: "Renter-Investor", sub: `DP + SIP corpus + deposit · ${equityCAGR}% CAGR`, value: current.netRentWorth, accent: "#16a34a" },
                        ].map((a) => {
                            const ratioMax = Math.max(current.netBuyWorth, current.netRentWorth);
                            return (
                                <div key={a.id} data-testid={`inv-side-${a.id}`} className="flex items-center gap-4">
                                    <div className="w-40 shrink-0">
                                        <div className="text-sm font-semibold text-[#0A1F44]">{a.label}</div>
                                        <div className="text-[10px] text-[#0A1F44]/55">{a.sub}</div>
                                    </div>
                                    <div className="flex-1 h-3 rounded-full bg-[#0A1F44]/6 overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${(a.value / ratioMax) * 100}%` }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 1, ease: EASE }}
                                            className="h-full rounded-full"
                                            style={{ backgroundColor: a.accent }}
                                        />
                                    </div>
                                    <div className="w-28 text-right font-display font-extrabold text-lg text-[#0A1F44] tabular-nums">{fmtINRCompact(a.value)}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-4 rounded-xl bg-[#F7F8FB] px-4 py-3 text-[11px] text-[#0A1F44]/65 leading-relaxed">
                        Assumes <strong className="text-[#0A1F44]">EMI ₹{Math.round(investmentOutputs.inputsEcho.emi).toLocaleString("en-IN")}</strong> baseline, equity invest at {equityCAGR}% p.a., property at {propertyCAGR.toFixed(1)}% CAGR, rent escalating 5% / yr, maintenance ₹6/sqft + 18% GST stepping up 5% / yr, 6-month deposit locked. Net of affordability segment <strong className="text-[#0A1F44]">{affordabilityOutputs.segment}</strong>.
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Pillar 2 — Buy vs Rent (LIVE)
// ─────────────────────────────────────────────────────────────────────
function BuyVsRent() {
    const { investmentOutputs } = useHomeIQ();
    const { ladder, buyVsRentBreakEvenYear, propertyCAGR, equityCAGR } = investmentOutputs;
    const checkpointYears = [5, 10, 15, 20];
    const rows = checkpointYears
        .map((y) => ladder.find((r) => r.year === y))
        .filter(Boolean)
        .map((r) => ({
            year: r.year,
            netBuy: r.netBuyWorth,
            netRent: r.netRentWorth,
        }));
    const max = Math.max(...rows.flatMap((r) => [r.netBuy, r.netRent]));

    return (
        <Reveal>
            <div data-testid="inv-pillar-buy-vs-rent" className="space-y-4">
                <PillarHead index={2} title="Buy vs Rent" sub="Side-by-side net worth — both scenarios spend the same EMI in cash." />

                {/* Assumptions strip — live */}
                <div data-testid="inv-assumptions" className="rounded-2xl border border-[#0A1F44]/10 bg-[#F7F8FB] p-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold mb-2">Live assumptions</div>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { k: "Rent escalation", v: "5% / yr" },
                            { k: "Maintenance", v: "₹6/sqft + 18% GST · 5%/yr" },
                            { k: "Equity CAGR (SIP)", v: `${equityCAGR}% p.a.` },
                            { k: "Property CAGR", v: `${propertyCAGR.toFixed(1)}%` },
                            { k: "Deposit", v: `6 months locked` },
                        ].map((a) => (
                            <span key={a.k} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#0A1F44]/10 text-[11px]">
                                <span className="text-[#0A1F44]/55 font-semibold">{a.k}</span>
                                <span className="text-[#0A1F44] font-semibold tabular-nums">{a.v}</span>
                            </span>
                        ))}
                    </div>
                </div>

                {/* Timeline */}
                <div data-testid="inv-vs-rent" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                    <h4 className="font-display font-extrabold text-xl text-[#0A1F44]">Net worth checkpoints</h4>
                    <p className="text-sm text-[#0A1F44]/65 mt-1 mb-5">Buyer = property − loan. Renter = invested DP + SIP corpus + deposit.</p>
                    <div className="space-y-4">
                        {rows.map((r) => (
                            <div key={r.year} data-testid={`inv-row-${r.year}`}>
                                <div className="flex items-baseline justify-between text-xs mb-1">
                                    <span className="text-[#0A1F44]/65 font-semibold">{r.year} years</span>
                                    <span className="text-[#0A1F44]/55">
                                        Advantage{" "}
                                        <span className={`font-bold ${r.netBuy >= r.netRent ? "text-[#16a34a]" : "text-[#dc2626]"}`}>
                                            {r.netBuy >= r.netRent ? "+" : "−"}{fmtINR(Math.abs(r.netBuy - r.netRent))}
                                        </span>{" "}
                                        {r.netBuy >= r.netRent ? "for buying" : "for renting"}
                                    </span>
                                </div>
                                <div className="flex gap-2 items-center">
                                    <span className="w-12 text-[10px] uppercase text-[#0A1F44]/55">Buy</span>
                                    <div className="flex-1 h-3 rounded-full bg-[#0A1F44]/6 overflow-hidden">
                                        <div className="h-full bg-[#1E6BFF]" style={{ width: `${(r.netBuy / max) * 100}%` }} />
                                    </div>
                                    <span className="w-20 text-right text-xs tabular-nums">{fmtINRCompact(r.netBuy)}</span>
                                </div>
                                <div className="flex gap-2 items-center mt-1">
                                    <span className="w-12 text-[10px] uppercase text-[#0A1F44]/55">Rent</span>
                                    <div className="flex-1 h-3 rounded-full bg-[#0A1F44]/6 overflow-hidden">
                                        <div className="h-full bg-[#16a34a]" style={{ width: `${(r.netRent / max) * 100}%` }} />
                                    </div>
                                    <span className="w-20 text-right text-xs tabular-nums">{fmtINRCompact(r.netRent)}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Break-even callout */}
                    <div data-testid="inv-break-even" className="mt-6 rounded-2xl bg-[#0A1F44] text-white p-5 flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Buy-vs-Rent break-even</div>
                            <div className="mt-1 font-display font-extrabold text-2xl">
                                {buyVsRentBreakEvenYear == null
                                    ? <>Beyond <span className="tabular-nums text-[#7FA8FF]">{rows[rows.length - 1].year}</span> years</>
                                    : <>Year <span className="tabular-nums text-[#7FA8FF]">{buyVsRentBreakEvenYear}</span></>}
                            </div>
                        </div>
                        <p className="text-sm text-white/75 max-w-md">
                            {buyVsRentBreakEvenYear == null
                                ? `Within the 20-year window, the renter-investor path stays ahead. Consider revisiting if rates, rent or market CAGR shift.`
                                : `From year ${buyVsRentBreakEvenYear} onwards, buyer net worth overtakes the renter-investor.`}
                        </p>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Pillar 3 — Cost of Waiting (LIVE)
// ─────────────────────────────────────────────────────────────────────
const WAIT_HORIZONS = [1, 2, 3, 5];

function CostOfWaiting() {
    const { costOfDelayHorizon, setCostOfDelayHorizon, investmentOutputs, rent, affordabilityOutputs } = useHomeIQ();
    const active = WAIT_HORIZONS.includes(costOfDelayHorizon) ? costOfDelayHorizon : 2;
    const propAppreciationMissed = investmentOutputs.costOfWaiting[active] ?? 0;
    // Cumulative rent paid during wait (5%/yr escalation, capped at the wait horizon)
    let rentPaid = 0;
    if (rent != null && rent > 0) {
        for (let y = 1; y <= active; y++) {
            rentPaid += rent * Math.pow(1.05, y - 1) * 12;
        }
    }
    const futureCost = affordabilityOutputs.max_affordable_property + propAppreciationMissed;
    const totalCost = rentPaid + propAppreciationMissed;
    return (
        <Reveal>
            <div data-testid="inv-pillar-cost-of-waiting" className="space-y-4">
                <PillarHead index={3} title="Cost of Waiting" sub='The most under-asked question: "What if I just wait?"' />

                {/* Horizon selector */}
                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold mr-1">Wait period</span>
                    {WAIT_HORIZONS.map((y) => (
                        <button
                            key={y}
                            type="button"
                            onClick={() => setCostOfDelayHorizon(y)}
                            data-testid={`inv-wait-${y}y`}
                            className={`px-4 py-1.5 rounded-full text-[11px] font-semibold transition-colors ${
                                active === y
                                    ? "bg-[#0A1F44] text-white"
                                    : "bg-[#0A1F44]/6 text-[#0A1F44]/70 hover:bg-[#0A1F44]/10"
                            }`}
                        >
                            {y} {y === 1 ? "year" : "years"}
                        </button>
                    ))}
                </div>

                {/* Delta tiles */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                        { k: "Future property cost", v: fmtINR(futureCost), sub: `at ${investmentOutputs.propertyCAGR.toFixed(1)}% CAGR` },
                        { k: "Rent paid during wait", v: fmtINR(rentPaid), sub: "5% / yr escalation" },
                        { k: "Appreciation missed", v: fmtINR(propAppreciationMissed), sub: "on un-bought home" },
                        { k: "Total cost of delay", v: fmtINR(totalCost), sub: "rent + appreciation" },
                    ].map((t) => (
                        <div key={t.k} data-testid={`inv-wait-tile-${t.k.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className="rounded-2xl border border-[#0A1F44]/10 bg-white p-5">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">{t.k}</div>
                            <div className="mt-2 font-display font-extrabold text-2xl text-[#0A1F44] tabular-nums">{t.v}</div>
                            <div className="text-[11px] text-[#0A1F44]/50 mt-1">{t.sub}</div>
                        </div>
                    ))}
                </div>

                {/* Total cost of delay */}
                <div data-testid="inv-total-cost-delay" className="rounded-3xl bg-[#0A1F44] text-white p-6 md:p-8 relative overflow-hidden">
                    <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
                    <div className="relative flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Total cost of delaying {active} {active === 1 ? "year" : "years"}</div>
                            <div className="mt-2 font-display font-black text-5xl md:text-6xl tracking-tight tabular-nums">{fmtINR(totalCost)}</div>
                            <p className="mt-2 text-sm text-white/65 max-w-md">Rent paid + appreciation missed at your selected market&apos;s CAGR. Excludes any rate drift effect on EMI.</p>
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-semibold">Live</div>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Pillar 4 — HomeIQ Recommendation (LIVE · Financial + Market + Lifestyle + Composite)
// ─────────────────────────────────────────────────────────────────────
const VERDICT_TONE = {
    Buy: { dot: "#16a34a", text: "#16a34a", border: "rgba(22,163,74,0.35)", bg: "rgba(22,163,74,0.12)" },
    "Considered Buy": { dot: "#1E6BFF", text: "#1E6BFF", border: "rgba(30,107,255,0.35)", bg: "rgba(30,107,255,0.12)" },
    Marginal: { dot: "#d97706", text: "#d97706", border: "rgba(217,119,6,0.35)", bg: "rgba(217,119,6,0.12)" },
    Wait: { dot: "#d97706", text: "#d97706", border: "rgba(217,119,6,0.35)", bg: "rgba(217,119,6,0.12)" },
    Rent: { dot: "#dc2626", text: "#dc2626", border: "rgba(220,38,38,0.35)", bg: "rgba(220,38,38,0.12)" },
    Strong: { dot: "#16a34a", text: "#16a34a", border: "rgba(22,163,74,0.35)", bg: "rgba(22,163,74,0.12)" },
    Solid: { dot: "#1E6BFF", text: "#1E6BFF", border: "rgba(30,107,255,0.35)", bg: "rgba(30,107,255,0.12)" },
    Weak: { dot: "#dc2626", text: "#dc2626", border: "rgba(220,38,38,0.35)", bg: "rgba(220,38,38,0.12)" },
    Comfortable: { dot: "#16a34a", text: "#16a34a", border: "rgba(22,163,74,0.35)", bg: "rgba(22,163,74,0.12)" },
    Stretch: { dot: "#d97706", text: "#d97706", border: "rgba(217,119,6,0.35)", bg: "rgba(217,119,6,0.12)" },
    Tight: { dot: "#dc2626", text: "#dc2626", border: "rgba(220,38,38,0.35)", bg: "rgba(220,38,38,0.12)" },
    Pending: { dot: "#0A1F44", text: "#0A1F44", border: "rgba(10,31,68,0.25)", bg: "rgba(10,31,68,0.06)" },
};

function PillarVerdictBadge({ verdict, confidence, dataTestid }) {
    const tone = VERDICT_TONE[verdict] ?? VERDICT_TONE.Pending;
    return (
        <span
            data-testid={dataTestid}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-bold"
            style={{ backgroundColor: tone.bg, color: tone.text, borderColor: tone.border }}
        >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tone.dot }} aria-hidden />
            {verdict}{confidence ? ` · ${confidence}` : ""}
        </span>
    );
}

const TILE_TONE = {
    Healthy: { dot: "#16a34a", text: "#16a34a", border: "rgba(22,163,74,0.30)", bg: "rgba(22,163,74,0.06)" },
    Stretch: { dot: "#d97706", text: "#d97706", border: "rgba(217,119,6,0.30)", bg: "rgba(217,119,6,0.06)" },
    Tight:   { dot: "#dc2626", text: "#dc2626", border: "rgba(220,38,38,0.30)", bg: "rgba(220,38,38,0.06)" },
    Pending: { dot: "#0A1F44", text: "#0A1F44", border: "rgba(10,31,68,0.25)", bg: "rgba(10,31,68,0.04)" },
};

function LifestyleMetricTile({ metric }) {
    const tone = TILE_TONE[metric.status] ?? TILE_TONE.Pending;
    return (
        <div
            data-testid={`inv-lifestyle-tile-${metric.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="rounded-xl p-3 border"
            style={{ backgroundColor: tone.bg, borderColor: tone.border }}
        >
            <div className="flex items-baseline justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/55 font-semibold">{metric.label}</div>
                <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                    style={{ color: tone.text }}
                >
                    <span className="w-1 h-1 rounded-full" style={{ backgroundColor: tone.dot }} aria-hidden />
                    {metric.status}
                </span>
            </div>
            <div className="mt-1.5 font-display font-extrabold text-2xl text-[#0A1F44] tabular-nums">
                {metric.valuePct.toFixed(0)}%
            </div>
            <p className="mt-1.5 text-[10px] text-[#0A1F44]/60 leading-snug">{metric.caption}</p>
        </div>
    );
}

function HomeIQRecommendation() {
    const { investmentOutputs, setActiveSection } = useHomeIQ();
    const rec = investmentOutputs.recommendation;
    const compTone = VERDICT_TONE[rec.composite.verdict] ?? VERDICT_TONE.Pending;
    const lifestyleMetrics = rec.lifestyle.metrics
        ? [rec.lifestyle.metrics.foir, rec.lifestyle.metrics.downCushion, rec.lifestyle.metrics.cashflowShock]
        : [];
    return (
        <Reveal>
            <div data-testid="inv-pillar-recommendation" className="space-y-4">
                <PillarHead index={4} title="HomeIQ Recommendation" sub="Three lenses on the same decision · one composite verdict." />

                {/* Composite hero */}
                <div
                    data-testid="inv-verdict-card"
                    className="rounded-3xl bg-[#0A1F44] text-white p-6 md:p-10 relative overflow-hidden"
                >
                    <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
                    <div className="relative">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <p className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">— HomeIQ verdict</p>
                            <span
                                data-testid="inv-confidence"
                                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[11px] font-bold"
                                style={{ backgroundColor: compTone.bg, color: compTone.text, borderColor: compTone.border }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: compTone.dot }} aria-hidden />
                                {rec.composite.confidence} confidence
                            </span>
                        </div>
                        <h3 className="mt-4 font-display font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-tight">
                            {rec.composite.verdict}
                        </h3>
                        <p className="mt-3 text-white/75 text-sm md:text-base max-w-3xl">
                            A weighted read across financial outcome, market opportunity and lifestyle context.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => setActiveSection("loan")}
                                data-testid="inv-cta-loan"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E6BFF] hover:bg-white hover:text-[#0A1F44] text-sm font-semibold transition-colors"
                            >
                                Plan the loan →
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveSection("vastu")}
                                data-testid="inv-cta-vastu"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors"
                            >
                                Read the Vastu
                            </button>
                        </div>
                    </div>
                </div>

                {/* 3 pillars */}
                <div className="grid md:grid-cols-3 gap-3">
                    <div data-testid="inv-rec-financial" className="rounded-2xl border border-[#0A1F44]/10 bg-white p-5">
                        <div className="flex items-baseline justify-between">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Financial outcome</div>
                            <PillarVerdictBadge verdict={rec.financial.verdict} confidence={rec.financial.confidence} dataTestid="inv-rec-financial-badge" />
                        </div>
                        <div className="mt-2 font-display font-extrabold text-xl text-[#0A1F44] tabular-nums">
                            {rec.financial.marginPct >= 0 ? "+" : ""}{rec.financial.marginPct.toFixed(1)}%
                        </div>
                        <p className="mt-2 text-xs text-[#0A1F44]/65 leading-relaxed">{rec.financial.summary}</p>
                    </div>
                    <div data-testid="inv-rec-market" className="rounded-2xl border border-[#0A1F44]/10 bg-white p-5">
                        <div className="flex items-baseline justify-between">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Market opportunity</div>
                            <PillarVerdictBadge verdict={rec.market.verdict} dataTestid="inv-rec-market-badge" />
                        </div>
                        <div className="mt-2 font-display font-extrabold text-xl text-[#0A1F44]">
                            {rec.market.area ?? "—"}
                        </div>
                        <p className="mt-2 text-xs text-[#0A1F44]/65 leading-relaxed">{rec.market.summary}</p>
                    </div>
                    <div data-testid="inv-rec-lifestyle" className="rounded-2xl border border-[#0A1F44]/10 bg-white p-5">
                        <div className="flex items-baseline justify-between">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Lifestyle context</div>
                            <PillarVerdictBadge verdict={rec.lifestyle.verdict} dataTestid="inv-rec-lifestyle-badge" />
                        </div>
                        <div className="mt-2 font-display font-extrabold text-xl text-[#0A1F44]">
                            Monthly cashflow
                        </div>
                        <p className="mt-2 text-xs text-[#0A1F44]/65 leading-relaxed">{rec.lifestyle.summary}</p>
                        {lifestyleMetrics.length > 0 && (
                            <div data-testid="inv-lifestyle-tiles" className="mt-3 grid grid-cols-3 gap-2">
                                {lifestyleMetrics.map((metric) => (
                                    <LifestyleMetricTile key={metric.label} metric={metric} />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────
export default function InvestmentInsightsTab() {
    const { investmentOutputs } = useHomeIQ();
    const blocked = investmentOutputs.blocked;
    return (
        <section data-testid="investment-tab" className="py-14 px-6 md:px-12 bg-white">
            <div className="max-w-7xl mx-auto space-y-10">
                <SectionHead
                    eyebrow="Investment insights"
                    title="A home is also a 20-year position."
                    sub="Four reads on the same decision — what you build, what you save, what you risk by waiting, and what we'd lean toward."
                />
                <BudgetContextStrip />
                {blocked ? (
                    <BlockedPanel />
                ) : (
                    <>
                        <WealthProjection />
                        <BuyVsRent />
                        <CostOfWaiting />
                        <HomeIQRecommendation />
                    </>
                )}
            </div>
        </section>
    );
}
